import React from "react";
import { useTranslation } from "react-i18next";
import { H2 } from "@/shared/ui";

export const TermsPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-nieve py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <H2 className="mb-8">{t("legal.terms.title")}</H2>

        <div className="bg-white rounded-lg shadow-lg p-8 prose max-w-none">
          <p className="text-titan mb-6">{t("legal.terms.lastUpdated")}</p>

          {/* Section 1: Acceptance */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.acceptance.title")}
          </h3>
          <p className="mb-4">{t("legal.terms.sections.acceptance.content")}</p>

          {/* Section 2: Description */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.description.title")}
          </h3>
          <p className="mb-4">{t("legal.terms.sections.description.intro")}</p>
          <ul className="list-disc pl-6 mb-4">
            <li>{t("legal.terms.sections.description.features.generation")}</li>
            <li>{t("legal.terms.sections.description.features.download")}</li>
            <li>{t("legal.terms.sections.description.features.storage")}</li>
            <li>{t("legal.terms.sections.description.features.editing")}</li>
          </ul>

          {/* Section 3: Legal Limitation */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.legalLimitation.title")}
          </h3>
          <p className="mb-4">
            <strong>
              {t("legal.terms.sections.legalLimitation.important")}
            </strong>{" "}
            {t("legal.terms.sections.legalLimitation.importantContent")}{" "}
            <strong>
              {t("legal.terms.sections.legalLimitation.draftsOrTemplates")}
            </strong>
            . {t("legal.terms.sections.legalLimitation.notLegalAdvice")}
          </p>
          <p className="mb-4">
            {t("legal.terms.sections.legalLimitation.recommendation")}
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              {t(
                "legal.terms.sections.legalLimitation.notResponsibleFor.accuracy"
              )}
            </li>
            <li>
              {t(
                "legal.terms.sections.legalLimitation.notResponsibleFor.suitability"
              )}
            </li>
            <li>
              {t(
                "legal.terms.sections.legalLimitation.notResponsibleFor.consequences"
              )}
            </li>
            <li>
              {t(
                "legal.terms.sections.legalLimitation.notResponsibleFor.errors"
              )}
            </li>
          </ul>

          {/* Section 4: User Account */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.userAccount.title")}
          </h3>
          <p className="mb-4">{t("legal.terms.sections.userAccount.intro")}</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              {t("legal.terms.sections.userAccount.responsibilities.password")}
            </li>
            <li>
              {t(
                "legal.terms.sections.userAccount.responsibilities.activities"
              )}
            </li>
            <li>
              {t(
                "legal.terms.sections.userAccount.responsibilities.unauthorized"
              )}
            </li>
          </ul>

          {/* Section 5: Plans and Payments */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.plansPayments.title")}
          </h3>
          <p className="mb-4">
            {t("legal.terms.sections.plansPayments.intro")}
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>Plan Free:</strong>{" "}
              {t("legal.terms.sections.plansPayments.plans.free").replace(
                "Free Plan: ",
                ""
              )}
            </li>
            <li>
              <strong>Plan Pro:</strong>{" "}
              {t("legal.terms.sections.plansPayments.plans.pro").replace(
                "Pro Plan: ",
                ""
              )}
            </li>
          </ul>
          <p className="mb-4">
            {t("legal.terms.sections.plansPayments.stripe")}
          </p>

          {/* Section 6: Acceptable Use */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.acceptableUse.title")}
          </h3>
          <p className="mb-4">
            {t("legal.terms.sections.acceptableUse.intro")}
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              {t("legal.terms.sections.acceptableUse.prohibited.illegal")}
            </li>
            <li>
              {t("legal.terms.sections.acceptableUse.prohibited.falseInfo")}
            </li>
            <li>
              {t("legal.terms.sections.acceptableUse.prohibited.ipViolation")}
            </li>
            <li>{t("legal.terms.sections.acceptableUse.prohibited.abuse")}</li>
            <li>{t("legal.terms.sections.acceptableUse.prohibited.resale")}</li>
            <li>
              {t("legal.terms.sections.acceptableUse.prohibited.attackAccess")}
            </li>
            <li>
              {t("legal.terms.sections.acceptableUse.prohibited.probing")}
            </li>
            <li>
              {t("legal.terms.sections.acceptableUse.prohibited.bypass")}
            </li>
            <li>
              {t("legal.terms.sections.acceptableUse.prohibited.automationAbuse")}
            </li>
          </ul>
          <p className="mb-4">
            {t("legal.terms.sections.acceptableUse.securityEnforcement")}
          </p>

          {/* Section 7: Intellectual Property */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.intellectualProperty.title")}
          </h3>
          <p className="mb-4">
            {t("legal.terms.sections.intellectualProperty.intro")}
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              {t("legal.terms.sections.intellectualProperty.retained.platform")}
            </li>
            <li>
              {t("legal.terms.sections.intellectualProperty.retained.design")}
            </li>
            <li>
              {t(
                "legal.terms.sections.intellectualProperty.retained.algorithms"
              )}
            </li>
          </ul>

          {/* Section 8: Privacy and Data */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.privacyData.title")}
          </h3>
          <p className="mb-4">
            {t("legal.terms.sections.privacyData.content")}{" "}
            <a href="/legal/privacy" className="text-tiber-500 hover:underline">
              {t("legal.terms.sections.privacyData.privacyPolicy")}
            </a>
            . {t("legal.terms.sections.privacyData.acceptance")}
          </p>

          {/* Section 9: Service Modifications */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.serviceModifications.title")}
          </h3>
          <p className="mb-4">
            {t("legal.terms.sections.serviceModifications.content")}
          </p>

          {/* Section 10: Termination */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.termination.title")}
          </h3>
          <p className="mb-4">
            {t("legal.terms.sections.termination.content")}
          </p>

          {/* Section 11: Liability Limitation */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.liabilityLimitation.title")}
          </h3>
          <p className="mb-4">
            {t("legal.terms.sections.liabilityLimitation.content")}
          </p>

          {/* Section 12: Applicable Law */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.applicableLaw.title")}
          </h3>
          <p className="mb-4">
            {t("legal.terms.sections.applicableLaw.content")}
          </p>

          {/* Section 13: Contact */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.terms.sections.contact.title")}
          </h3>
          <p className="mb-4">{t("legal.terms.sections.contact.intro")}</p>
          <p className="mb-4">
            <strong>{t("legal.terms.sections.contact.email")}</strong>{" "}
            <a
              href="mailto:nolimstudios@gmail.com"
              className="text-tiber-500 hover:underline"
            >
              Nolim Studios Support
            </a>
          </p>
          <p className="mb-4">
            <strong>{t("legal.terms.sections.contact.company")}</strong>
            <br />
            {t("legal.terms.sections.contact.companySubtitle")}
          </p>

          <div className="mt-8 p-4 bg-tiber-100 rounded-lg">
            <p className="text-sm text-titan">
              {t("legal.terms.sections.acknowledgment")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
